/**
 * FinOps API Service
 * 
 * Service for fetching purchase request data from the FinOps (Access Financials Operations) API.
 * This service routes requests through the backend (FE -> BE -> FinOps API) for security.
 * This service is used when a company has the FinOps integration configured.
 */

import type { FinOpsPurchaseRequestHeader, FinOpsPurchaseRequestDetail, FinOpsApiConfig, FinOpsTimesheetEntry } from '@/types/finops.types';
import type { PurchaseRequest, PurchaseRequestStatus } from '@/types/purchase-request';
import { getIntegrationsFromStorage } from '@/hooks/use-product-integrations';
import { getApiBaseUrl, getDataMode } from './data-service';
import { apiFetch } from './api-error-handler';

const FINOPS_PRODUCT_CODE = 'access-financials-operations';

/**
 * Get the FinOps API configuration if the integration is set up for a specific company
 * Returns null if no FinOps integration is configured for the company
 * @param companyId - Optional company ID to filter integrations. If not provided, returns first connected integration.
 */
export function getFinOpsConfig(companyId?: number): FinOpsApiConfig | null {
  const integrations = getIntegrationsFromStorage();
  
  const finOpsIntegration = integrations.find(
    (integration) => 
      integration.productCode === FINOPS_PRODUCT_CODE && 
      integration.status === 'connected' &&
      (companyId ? integration.companyId === String(companyId) : true)
  );
  
  if (!finOpsIntegration) {
    return null;
  }
  
  return {
    baseUrl: getApiBaseUrl(),
    instanceId: finOpsIntegration.instanceId,
  };
}

/**
 * Check if FinOps integration is available for a specific company
 * @param companyId - Optional company ID to check. If not provided, checks for any connected integration.
 */
export function isFinOpsIntegrationAvailable(companyId?: number): boolean {
  return getFinOpsConfig(companyId) !== null;
}

/**
 * Map FinOps API status to internal PurchaseRequestStatus
 */
function mapFinOpsStatus(status: string): PurchaseRequestStatus {
  switch (status) {
    case 'Approved':
    case 'Ordered':
    case 'Received':
      return 'Approved';
    case 'Rejected':
    case 'Cancelled':
      return 'Rejected';
    case 'NotSet':
    case 'Draft':
    case 'Submitted':
    default:
      return 'Pending';
  }
}

/**
 * Map FinOps API response to internal PurchaseRequest format
 */
function mapFinOpsToPurchaseRequest(
  header: FinOpsPurchaseRequestHeader,
  projectId: number
): PurchaseRequest {
  return {
    id: `finops-${header.primaryKey}`,
    requestId: header.reference || `PR-${header.primaryKey}`,
    description: header.description || '',
    status: mapFinOpsStatus(header.status),
    amount: header.total || 0,
    supplier: header.recipient || 'Unknown',
    date: header.lastEdited || new Date().toISOString(),
    projectId: String(projectId),
    requestedBy: header.initiator || 'Unknown',
    approvedBy: header.status === 'Approved' ? header.initiator : undefined,
    approvedDate: header.status === 'Approved' ? header.lastEdited : undefined,
    category: header.type || 'General',
    notes: header.orderNumber ? `Order Number: ${header.orderNumber}` : undefined,
    currency: 'GBP',
    documents: [],
    lines: [],
  };
}

/**
 * Fetch purchase requests from the FinOps API via the backend
 * Routes through: Frontend -> Backend -> FinOps API
 * 
 * @param projectCode - The project code to fetch purchase requests for
 * @param projectId - The internal project ID for mapping
 * @param companyId - Optional company ID to use for determining which FinOps instance to query (demo mode)
 * @param instanceId - Optional instance ID to use directly (live mode - from company.instanceId)
 * @returns Array of PurchaseRequest objects or null if integration is not available
 */
export async function fetchFinOpsPurchaseRequests(
  projectCode: string,
  projectId: number,
  companyId?: number,
  instanceId?: string
): Promise<PurchaseRequest[] | null> {
  const dataMode = getDataMode();
  
  // In live mode, use the provided instanceId directly
  // In demo mode, look up the config from localStorage
  let config: FinOpsApiConfig | null;
  if (dataMode === 'live' && instanceId) {
    config = {
      baseUrl: getApiBaseUrl(),
      instanceId: instanceId,
    };
  } else {
    config = getFinOpsConfig(companyId);
  }
  
  if (!config) {
    return null;
  }
  
  if (dataMode !== 'live') {
    return null;
  }
  
  // Validate that instanceId is present - required for FinOps API
  if (!config.instanceId) {
    throw new Error('FinOps integration requires an instanceId. Please configure the company with a valid FinOps instance.');
  }
  
  try {
    // Call the backend endpoint which proxies to FinOps API
    // Note: config.baseUrl already includes /api, so we don't add it again
    const url = `${config.baseUrl}/finops/purchase-requests?projectCode=${encodeURIComponent(projectCode)}`;
    
    // Use X-FinOps-Instance-Id header for the company's FinOps instance ID
    // This is separate from X-Instance-Id which is used for platform subscription lookup
    const data = await apiFetch<FinOpsPurchaseRequestHeader[] | { items?: FinOpsPurchaseRequestHeader[]; data?: FinOpsPurchaseRequestHeader[]; value?: FinOpsPurchaseRequestHeader[]; $values?: FinOpsPurchaseRequestHeader[] }>(url, {
      method: 'GET',
      headers: {
        'X-FinOps-Instance-Id': config.instanceId,
      },
    });
    
    let headers: FinOpsPurchaseRequestHeader[] = [];
    if (Array.isArray(data)) {
      headers = data;
    } else if (data && typeof data === 'object') {
      // Try various common response wrapper formats
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.list)) {
        // FinOps API returns data in 'list' property
        headers = dataObj.list as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.items)) {
        headers = dataObj.items as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.data)) {
        headers = dataObj.data as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.value)) {
        headers = dataObj.value as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.$values)) {
        headers = dataObj.$values as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.results)) {
        headers = dataObj.results as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.records)) {
        headers = dataObj.records as FinOpsPurchaseRequestHeader[];
      }
    }
    
    return headers.map((header) => mapFinOpsToPurchaseRequest(header, projectId));
  } catch (error) {
    console.error('Error fetching FinOps purchase requests:', error);
    throw error;
  }
}

/**
 * Fetch purchase request line details from the FinOps API via the backend
 * Routes through: Frontend -> Backend -> FinOps API
 * 
 * @param projectCode - The project code to fetch line details for
 * @param instanceId - The FinOps instance ID
 * @returns Array of FinOpsPurchaseRequestDetail objects or null if not available
 */
export async function fetchFinOpsPurchaseRequestDetails(
  projectCode: string,
  instanceId: string
): Promise<FinOpsPurchaseRequestDetail[] | null> {
  const dataMode = getDataMode();
  
  if (dataMode !== 'live') {
    return null;
  }
  
  if (!instanceId) {
    return null;
  }
  
  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/finops/purchase-request-details?projectCode=${encodeURIComponent(projectCode)}`;
    
    const data = await apiFetch<FinOpsPurchaseRequestDetail[] | { list?: FinOpsPurchaseRequestDetail[]; items?: FinOpsPurchaseRequestDetail[]; data?: FinOpsPurchaseRequestDetail[] }>(url, {
      method: 'GET',
      headers: {
        'X-FinOps-Instance-Id': instanceId,
      },
    });
    
    let details: FinOpsPurchaseRequestDetail[] = [];
    if (Array.isArray(data)) {
      details = data;
    } else if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.list)) {
        details = dataObj.list as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.items)) {
        details = dataObj.items as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.data)) {
        details = dataObj.data as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.value)) {
        details = dataObj.value as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.$values)) {
        details = dataObj.$values as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.results)) {
        details = dataObj.results as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.records)) {
        details = dataObj.records as FinOpsPurchaseRequestDetail[];
      }
    }
    
    return details;
  } catch (error) {
    console.error('Error fetching FinOps purchase request details:', error);
    throw error;
  }
}

/**
 * Fetch timesheet entries from the FinOps API via the backend
 * Routes through: Frontend -> Backend -> FinOps API
 * 
 * @param projectCode - The project code to fetch timesheets for
 * @param instanceId - The FinOps instance ID (from company.instanceId)
 * @returns Array of FinOpsTimesheetEntry objects or null if not available
 */
export async function fetchFinOpsTimesheets(
  projectCode: string,
  instanceId: string
): Promise<FinOpsTimesheetEntry[] | null> {
  const dataMode = getDataMode();
  
  if (dataMode !== 'live') {
    return null;
  }
  
  if (!instanceId) {
    return null;
  }
  
  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/finops/timesheets?projectCode=${encodeURIComponent(projectCode)}`;
    
    const data = await apiFetch<FinOpsTimesheetEntry[] | { list?: FinOpsTimesheetEntry[]; items?: FinOpsTimesheetEntry[]; data?: FinOpsTimesheetEntry[] }>(url, {
      method: 'GET',
      headers: {
        'X-FinOps-Instance-Id': instanceId,
      },
    });
    
    let entries: FinOpsTimesheetEntry[] = [];
    if (Array.isArray(data)) {
      entries = data;
    } else if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.list)) {
        entries = dataObj.list as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.items)) {
        entries = dataObj.items as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.data)) {
        entries = dataObj.data as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.value)) {
        entries = dataObj.value as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.$values)) {
        entries = dataObj.$values as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.results)) {
        entries = dataObj.results as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.records)) {
        entries = dataObj.records as FinOpsTimesheetEntry[];
      }
    }
    
    return entries;
  } catch (error) {
    console.error('Error fetching FinOps timesheets:', error);
    throw error;
  }
}
